import { useEditor, EditorContent } from "@tiptap/react";
import { Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { forwardRef, useImperativeHandle, useEffect } from "react";

// Custom mark for AI-inserted text (track-changes)
const AIInsert = Mark.create({
    name: "aiInsert",
    inclusive: false,
    addAttributes() {
        return {
            // The prose this AI text replaced, so "Reject" can put it back.
            // Empty for pure insertions (nothing was displaced).
            original: {
                default: "",
                parseHTML: (el) => el.getAttribute("data-ai-original") || "",
                renderHTML: (attrs) => (attrs.original ? { "data-ai-original": attrs.original } : {}),
            },
        };
    },
    parseHTML() {
        return [{ tag: "span[data-ai-insert]" }];
    },
    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { "data-ai-insert": "true", class: "ai-insert" }), 0];
    },
    addCommands() {
        return {
            setAIInsert: () => ({ commands }) => commands.setMark(this.name),
            unsetAIInsert: () => ({ commands }) => commands.unsetMark(this.name),
        };
    },
});

// Custom mark for proofread suggestions (inline highlight)
const ProofreadIssue = Mark.create({
    name: "proofreadIssue",
    inclusive: false,
    addAttributes() {
        return {
            suggestion: { default: "" },
        };
    },
    parseHTML() {
        return [{ tag: "span[data-proofread]" }];
    },
    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { "data-proofread": "true", class: "proofread-issue" }), 0];
    },
});

const RichEditor = forwardRef(function RichEditor(
    { content, onChange, placeholder = "Begin your chapter…", autofocus = false, focusMode = false },
    ref
) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Placeholder.configure({ placeholder }),
            AIInsert,
            ProofreadIssue,
        ],
        content: content || "",
        editorProps: {
            attributes: {
                class: "editor-content prose-quill focus:outline-none min-h-[60vh]",
                "data-testid": "manuscript-editor",
            },
        },
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        autofocus,
    });

    // sync external content changes (e.g. on chapter switch)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (content !== current) {
            editor.commands.setContent(content || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, editor]);

    useImperativeHandle(ref, () => ({
        getEditor: () => editor,
        getSelectedText: () => {
            if (!editor) return "";
            const { from, to } = editor.state.selection;
            return editor.state.doc.textBetween(from, to, "\n\n");
        },
        hasSelection: () => {
            if (!editor) return false;
            const { from, to } = editor.state.selection;
            return to > from;
        },
        getPlainText: () => editor?.getText() || "",
        insertAtCursor: (text) => {
            if (!editor) return;
            // Insert as AI-tracked content (track-changes)
            editor.chain().focus()
                .insertContent({ type: "text", text, marks: [{ type: "aiInsert" }] })
                .run();
        },
        insertParagraphAfter: (text) => {
            if (!editor) return;
            // Insert at end-of-document; preserve paragraph breaks
            const paragraphs = text.split(/\n{2,}/).filter(Boolean);
            const nodes = paragraphs.map(p => ({
                type: "paragraph",
                content: [{ type: "text", text: p, marks: [{ type: "aiInsert" }] }],
            }));
            editor.chain().focus("end").insertContent(nodes).run();
        },
        replaceSelectionWith: (text) => {
            if (!editor) return;
            const { from, to } = editor.state.selection;
            if (from === to) {
                editor.chain().focus().insertContent({ type: "text", text, marks: [{ type: "aiInsert" }] }).run();
                return;
            }
            // Remember what we are about to displace so Reject can restore it.
            const original = editor.state.doc.textBetween(from, to, "\n\n", " ");
            editor.chain().focus()
                .deleteSelection()
                .insertContent({ type: "text", text, marks: [{ type: "aiInsert", attrs: { original } }] })
                .run();
        },
        acceptAllAIInserts: () => {
            if (!editor) return;
            editor.chain().focus().selectAll().unsetMark("aiInsert").run();
            editor.commands.setTextSelection({ from: 0, to: 0 });
        },
        rejectAllAIInserts: () => {
            if (!editor) return;
            // Collect every AI-marked text node, keeping the prose it displaced.
            const { state } = editor;
            const nodes = [];
            state.doc.descendants((node, pos) => {
                const mark = node.isText && node.marks.find(m => m.type.name === "aiInsert");
                if (mark) {
                    nodes.push({ from: pos, to: pos + node.nodeSize, original: mark.attrs?.original || "" });
                }
            });
            // Merge adjacent nodes that came from the same replacement, so the
            // original prose is restored once rather than per fragment.
            const ranges = [];
            for (const n of nodes) {
                const prev = ranges[ranges.length - 1];
                if (prev && prev.to === n.from && prev.original === n.original) prev.to = n.to;
                else ranges.push({ ...n });
            }
            // Work backwards so earlier positions stay valid.
            ranges.sort((a, b) => b.from - a.from).forEach(r => {
                if (r.original) {
                    editor.chain()
                        .insertContentAt({ from: r.from, to: r.to }, r.original)
                        .run();
                } else {
                    editor.chain().setTextSelection({ from: r.from, to: r.to }).deleteSelection().run();
                }
            });
            editor.commands.setTextSelection({ from: 0, to: 0 });
        },
        countAIInserts: () => {
            if (!editor) return 0;
            let n = 0;
            editor.state.doc.descendants((node) => {
                if (node.isText && node.marks.some(m => m.type.name === "aiInsert")) n += 1;
            });
            return n;
        },
        highlightProofreadIssues: (terms) => {
            if (!editor) return 0;
            // remove existing
            editor.chain().focus().selectAll().unsetMark("proofreadIssue").run();
            editor.commands.setTextSelection({ from: 0, to: 0 });
            const docText = editor.getText();
            let found = 0;
            for (const term of terms) {
                if (!term || term.length < 2) continue;
                const idx = docText.toLowerCase().indexOf(term.toLowerCase());
                if (idx === -1) continue;
                const from = idx + 1; // +1 because tiptap positions start at 1
                const to = from + term.length;
                editor.chain().setTextSelection({ from, to })
                    .setMark("proofreadIssue", { suggestion: "" }).run();
                editor.commands.setTextSelection({ from: 0, to: 0 });
                found += 1;
            }
            return found;
        },
        applyProofreadFix: (term, suggestion) => {
            if (!editor || !term) return false;
            const docText = editor.getText();
            const idx = docText.toLowerCase().indexOf(term.toLowerCase());
            if (idx === -1) return false;
            const from = idx + 1;
            const to = from + term.length;
            editor.chain().focus()
                .setTextSelection({ from, to })
                .deleteSelection()
                .insertContent({ type: "text", text: suggestion, marks: [{ type: "aiInsert" }] })
                .run();
            return true;
        },
        clearProofreadIssues: () => {
            if (!editor) return;
            editor.chain().focus().selectAll().unsetMark("proofreadIssue").run();
            editor.commands.setTextSelection({ from: 0, to: 0 });
        },
        wordCount: () => {
            const t = editor?.getText() || "";
            return t.trim().split(/\s+/).filter(Boolean).length;
        },
    }), [editor]);

    return (
        <div className={focusMode ? "focus-mode-editor" : ""}>
            <EditorContent editor={editor} />
        </div>
    );
});

export default RichEditor;
